import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Menu, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Animated Logos Component
const AnimatedLogos = () => {
  const [animationState, setAnimationState] = useState('initial');
  const timeoutRefs = useRef([]);

  useEffect(() => {
    const runAnimation = () => {
      const t1 = setTimeout(() => {
        setAnimationState('separated');
        
        const t2 = setTimeout(() => {
          setAnimationState('combining');
          
          const t3 = setTimeout(() => {
            setAnimationState('initial');
            const t4 = setTimeout(runAnimation, 3000);
            timeoutRefs.current.push(t4);
          }, 1500);
          timeoutRefs.current.push(t3);
        }, 3000);
        timeoutRefs.current.push(t2);
      }, 1500);
      timeoutRefs.current.push(t1);
    };
    
    const initialTimeout = setTimeout(runAnimation, 1000);
    timeoutRefs.current.push(initialTimeout);
    
    return () => {
      timeoutRefs.current.forEach(timeout => clearTimeout(timeout));
    };
  }, []);
  
  return (
    <div className="flex items-center justify-center h-12">
      <div className="relative w-full max-w-xs h-12">
        {/* Main PCRA Logo */}
        <div 
          className={`absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 
            transition-all duration-1000 ease-in-out
            ${animationState === 'initial' ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}
            ${animationState === 'combining' ? 'opacity-90 scale-90' : ''}
            z-20`}
        >
          <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xl font-bold px-5 py-2 rounded-lg shadow-lg">
            PCRA
          </div>
        </div>
        
        {/* MedScore Logo */}
        <div 
          className={`absolute top-1/2 transform -translate-y-1/2 
            transition-all duration-1000 ease-in-out
            ${animationState === 'initial' ? 'left-1/2 -translate-x-1/2 opacity-0 scale-0' : 
              animationState === 'separated' ? 'left-1/4 -translate-x-1/2 opacity-100 scale-100' :
              'left-1/3 -translate-x-1/2 opacity-70 scale-90'}
            z-10`}
        >
          <div className="flex flex-col items-center">
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full blur-md opacity-40"></div>
              <div className="bg-slate-800 w-14 h-14 rounded-full flex items-center justify-center shadow-xl relative z-10 border border-blue-500/30">
                <span className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">MS</span>
              </div>
            </div>
            <span className={`mt-1 text-white font-semibold text-xs transition-all duration-700 
              ${animationState === 'separated' ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}>
              MedScore
            </span>
          </div>
        </div>
        
        {/* SafeCredits Logo */}
        <div 
          className={`absolute top-1/2 transform -translate-y-1/2 
            transition-all duration-1000 ease-in-out
            ${animationState === 'initial' ? 'left-1/2 -translate-x-1/2 opacity-0 scale-0' : 
              animationState === 'separated' ? 'left-3/4 -translate-x-1/2 opacity-100 scale-100' :
              'left-2/3 -translate-x-1/2 opacity-70 scale-90'}
            z-10`}
        >
          <div className="flex flex-col items-center">
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full blur-md opacity-40"></div>
              <div className="bg-slate-800 w-14 h-14 rounded-full flex items-center justify-center shadow-xl relative z-10 border border-purple-500/30">
                <span className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">SC</span>
              </div>
            </div>
            <span className={`mt-1 text-white font-semibold text-xs transition-all duration-700 
              ${animationState === 'separated' ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}>
              SafeCredits
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Custom NavLink with glassmorphism
const NavLink = ({ children, onClick, isActive }) => {
  return (
    <button 
      onClick={onClick}
      className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg
        ${isActive 
          ? 'text-white bg-white/10' 
          : 'text-slate-300 hover:text-white hover:bg-white/5'
        }`}
    >
      {children}
      {isActive && (
        <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-blue-500/20 to-purple-500/20 -z-10"></div>
      )}
    </button>
  );
};

// Dropdown Component
const Dropdown = ({ isOpen, setIsOpen, title, items, onItemClick }) => {
  return (
    <div className="relative">
      <button
        type="button"
        className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-slate-300 hover:text-white hover:bg-white/5 rounded-lg transition-all duration-300"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(prev => !prev);
        }}
        onMouseEnter={() => setIsOpen(true)}
      >
        {title}
        <ChevronDown
          className={`w-4 h-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div
          className="absolute top-full left-0 mt-2 py-2 w-48 rounded-xl glass border border-white/10 shadow-2xl z-50"
          onMouseLeave={() => setIsOpen(false)}
          style={{
            background: 'rgba(15, 23, 42, 0.95)',
            backdropFilter: 'blur(20px)',
          }}
        >
          {items.map((item, index) => (
            <button
              key={item.path}
              onClick={() => {
                onItemClick(item.path);
                setIsOpen(false);
              }}
              className="block w-full text-left px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/10"
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};


// Mobile Navigation Item
const MobileNavItem = ({ label, onClick }) => (
  <button 
    className="block w-full text-left py-2 text-slate-300 hover:text-white transition-colors"
    onClick={onClick}
  >
    {label}
  </button>
);

// Header Component
export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [careersOpen, setCareersOpen] = useState(false);
  const [activeLink, setActiveLink] = useState('home');
  const [isScrolled, setIsScrolled] = useState(false);
    const navigate = useNavigate();

  const solutions = [
    { label: 'MedScore', path: '/Medscore' },
    { label: 'SafeCredits', path: '/SafeCredits' }
  ];
  
  const menuItems = [
    { label: 'Work Culture', path: '/Culture' },
    { label: 'Life@MedScore', path: '/Life' },
    { label: 'Current Openings', path: '/Openings' }
  ];
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
 const scrollToSection = (id) => {
  setActiveLink(id);

  if (window.location.pathname !== '/') {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById(id);
      el?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  } else {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }

  setIsMenuOpen(false);
  setSolutionsOpen(false);
  setCareersOpen(false);
};
// useEffect(() => {
//   const close = () => {
//     setSolutionsOpen(false);
//     setCareersOpen(false);
//   };
//   document.addEventListener('click', close);
//   return () => document.removeEventListener('click', close);
// }, []);

  
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Syne:wght@400;500;600;700;800&display=swap');
        
        * {
          font-family: 'Syne', sans-serif;
        }
        
        .mono {
          font-family: 'Space Mono', monospace;
        }
        
        .glass {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(20px);
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'glass border-b border-white/10 py-3' 
          : 'bg-transparent py-4'
      }`}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <div className="flex items-center">
                <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xl font-bold px-5 py-2 rounded-lg shadow-lg">
            PCRA
          </div>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-2">
              <NavLink onClick={() => scrollToSection('home')} isActive={activeLink === 'home'}>
                Home
              </NavLink>
              
              <NavLink onClick={() => scrollToSection('about')} isActive={activeLink === 'about'}>
                About Us
              </NavLink>
              
              <Dropdown 
                isOpen={solutionsOpen}
                setIsOpen={setSolutionsOpen}
                title="Solutions"
                items={solutions}
                onItemClick={(path) => {
  navigate(path);
  setActiveLink('solutions');
}}

              />
              
              <NavLink onClick={() => scrollToSection('works')} isActive={activeLink === 'works'}>
                How It Works
              </NavLink>
              
              <NavLink onClick={() => scrollToSection('blogs')} isActive={activeLink === 'blogs'}>
                Blogs
              </NavLink>
              
              <NavLink onClick={() => scrollToSection('news')} isActive={activeLink === 'news'}>
                News
              </NavLink>
              
              <Dropdown 
                isOpen={careersOpen}
                setIsOpen={setCareersOpen}
                title="Careers"
                items={menuItems}
                onItemClick={(path) => {
  navigate(path);
  setActiveLink('careers');
}}

              />
              
              <NavLink onClick={() => scrollToSection('contact')} isActive={activeLink === 'contact'}>
                Contact
              </NavLink>
            </nav>
            
            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)} 
              className="lg:hidden p-2 rounded-lg glass hover:bg-white/10 transition-colors"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
        
        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden mt-4 glass border-t border-white/10">
            <nav className="max-w-7xl mx-auto px-6 py-4 space-y-2">
              <MobileNavItem label="Home" onClick={() => scrollToSection('home')} />
              <MobileNavItem label="About Us" onClick={() => scrollToSection('about')} />
              
              <div className="space-y-2">
                <button 
                  className="flex items-center justify-between w-full text-left text-slate-300 hover:text-white py-2"
                  onClick={() => setSolutionsOpen(!solutionsOpen)}
                >
                  <span>Solutions</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${solutionsOpen ? 'rotate-180' : ''}`} />
                </button>
                {solutionsOpen && (
                  <div className="pl-4 space-y-2">
                    {solutions.map((item) => (
                      <button 
                        key={item.path}
                        onClick={() => {
  navigate(item.path);
  setIsMenuOpen(false);
  setSolutionsOpen(false);
}}

                        className="block w-full text-left py-2 text-slate-400 hover:text-white"
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <MobileNavItem label="How It Works" onClick={() => scrollToSection('works')} />
              <MobileNavItem label="Blogs" onClick={() => scrollToSection('blogs')} />
              <MobileNavItem label="News" onClick={() => scrollToSection('news')} />
              
              <div className="space-y-2">
                <button 
                  className="flex items-center justify-between w-full text-left text-slate-300 hover:text-white py-2"
                  onClick={() => setCareersOpen(!careersOpen)}
                >
                  <span>Careers</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${careersOpen ? 'rotate-180' : ''}`} />
                </button>
                {careersOpen && (
                  <div className="pl-4 space-y-2">
                    {menuItems.map((item) => (
                      <button 
                        key={item.path}
                        onClick={() => {
  navigate(item.path);
  setIsMenuOpen(false);
  setCareersOpen(false);
}}

                        className="block w-full text-left py-2 text-slate-400 hover:text-white"
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <MobileNavItem label="Contact" onClick={() => scrollToSection('contact')} />
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

// Demo Component
export default function HeaderDemo() {
  return (
    <div >
      <Header />
      
   
    </div>
  );
}